import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DealCard from "../kanban/DealCard";
import { CreateDealModal } from "../kanban/CreateDealModal";
import { EditDealModal } from "../kanban/EditDealModal";
import { PipelinesAPI, type PipelineDto, type Stage } from "../../api/pipelinesApi";
import { getClients, type ClientDto } from "../../api/clients";
import { UsersAPI, type UserDto } from "../../api/usersApi";
import { DealsAPI, type Deal } from "../../api/deals";
import DeleteRequestsButton from "./DeleteRequestsModal";
import KaspiIssueModal from "../kaspi/KaspiIssueModal";
import { getPermissions } from "../../hooks/usePermissions";

const POLL_MS = 4000;

// ── Notification toast ────────────────────────────────────────
interface Toast {
  id: number;
  deal: Deal;
}

function NewDealToast({ toasts, clients, onDismiss, onOpen }: {
  toasts: Toast[];
  clients: ClientDto[];
  onDismiss: (id: number) => void;
  onOpen: (deal: Deal) => void;
}) {
  if (!toasts.length) return null;
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column-reverse", gap: 10 }}>
      {toasts.map((t) => {
        const client = clients.find((c) => c.id === t.deal.clientId);
        const name = client ? `${client.firstName ?? ""} ${client.lastName ?? ""}`.trim() : "Новый клиент";
        return (
          <div key={t.id} style={{
            background: "#0f1117", border: "1px solid #6366f1",
            borderRadius: 12, padding: "12px 14px", width: 280,
            boxShadow: "0 8px 30px rgba(99,102,241,0.3)",
            display: "flex", gap: 12, alignItems: "flex-start",
            animation: "slideIn 0.25s ease",
          }}>
            <div style={{ fontSize: 22, flexShrink: 0, marginTop: 1 }}>🛒</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#818cf8", marginBottom: 2 }}>Новый заказ с сайта</div>
              <div style={{ fontSize: 13, color: "#f9fafb", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {t.deal.title}
              </div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{name}</div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button onClick={() => onOpen(t.deal)} style={{
                  background: "#6366f1", color: "#fff", border: "none",
                  borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer", fontWeight: 600,
                }}>Открыть</button>
                <button onClick={() => onDismiss(t.id)} style={{
                  background: "rgba(255,255,255,0.06)", color: "#9ca3af", border: "1px solid #374151",
                  borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer",
                }}>Закрыть</button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Kanban() {
  const [pipelines, setPipelines] = useState<PipelineDto[]>([]);
  const hexOk = (c?: string | null) => (c && /^#[0-9a-fA-F]{6}$/.test(c) ? c : "#6366f1");
  const [activePipeline, setActivePipeline] = useState<number | null>(null);

  const [stages, setStages] = useState<Stage[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [clients, setClients] = useState<ClientDto[]>([]);
  const [users, setUsers] = useState<UserDto[]>([]);

  const [editDeal, setEditDeal] = useState<Deal | null>(null);
  // заказ Kaspi выдаётся только по коду покупателя: вместо обычного завершения открываем окно выдачи
  const [kaspiIssueDeal, setKaspiIssueDeal] = useState<Deal | null>(null);
  const isKaspiPipeline = (pid: number | null) => /kaspi|каспи/i.test(pipelines.find((p) => p.id === pid)?.name ?? "");
  const location = useLocation();
  const navigate = useNavigate();
  const [createStageId, setCreateStageId] = useState<number | null>(null);
  const [search, setSearch] = useState("");

  // Ссылка /deals?deal=ID (например, из окна входящего звонка) открывает карточку сделки
  useEffect(() => {
    const id = Number(new URLSearchParams(location.search).get("deal"));
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const [dealRes, clientsRes] = await Promise.all([DealsAPI.getById(id), getClients()]);
        if (cancelled) return;
        setClients(clientsRes.data);
        setEditDeal(dealRes.data);
      } catch {
        // сделка не найдена — просто остаёмся на доске
      } finally {
        if (!cancelled) navigate("/deals", { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [location.search, navigate]);

  const [isDragging, setIsDragging] = useState(false);
  const [draggedDeal, setDraggedDeal] = useState<Deal | null>(null);
  const [dragOverStage, setDragOverStage] = useState<number | null>(null);
  const [dragOverFinalZone, setDragOverFinalZone] = useState<"won" | "lost" | null>(null);

  const [expandedStages, setExpandedStages] = useState<Set<number>>(new Set());
  const isMobile = () => window.innerWidth <= 768;
  const toggleStage = (id: number) => {
    setExpandedStages(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const [newDealIds, setNewDealIds] = useState<Set<number>>(new Set());
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastCounter = useRef(0);
  const knownDealIds = useRef<Set<number>>(new Set());
  const activePipelineRef = useRef<number | null>(null);

  // ── Load ─────────────────────────────────────────────────────
  const loadData = useCallback(async (pipelineId: number, silent = false) => {
    const [stagesList, dealsList, clientsList] = await Promise.all([
      PipelinesAPI.getStages(pipelineId),
      DealsAPI.getByPipeline(pipelineId),
      getClients(),
    ]);

    const activeDeals: Deal[] = dealsList.data.filter((d: Deal) => d.status === "active");

    if (!silent) {
      setStages(stagesList);
      setClients(clientsList.data);
      knownDealIds.current = new Set(activeDeals.map((d) => d.id));
      setDeals(activeDeals);
      return;
    }

    // Silent poll — detect new deals
    const newOnes = activeDeals.filter((d) => !knownDealIds.current.has(d.id));
    if (newOnes.length > 0) {
      const freshNewIds = new Set(newOnes.map((d) => d.id));
      setNewDealIds((prev) => { const s = new Set(prev); freshNewIds.forEach((id) => s.add(id)); return s; });

      // Add toasts
      const newToasts: Toast[] = newOnes.map((d) => ({ id: ++toastCounter.current, deal: d }));
      setToasts((prev) => [...prev, ...newToasts]);

      // Auto-dismiss after 8s
      newToasts.forEach((t) => {
        setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== t.id)), 8000);
      });

      newOnes.forEach((d) => knownDealIds.current.add(d.id));
      setStages(stagesList);
      setClients(clientsList.data);
      setDeals(activeDeals);
    } else {
      // Still update in case amounts/stages changed
      setDeals(activeDeals);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const list = await PipelinesAPI.getAll();
      setPipelines(list);
      if (list[0]) setActivePipeline(list[0].id);
    };
    init();
  }, []);

  useEffect(() => {
    if (!activePipeline) return;
    activePipelineRef.current = activePipeline;
    loadData(activePipeline, false);

    // Load users once
    UsersAPI.getAll().then(async (allUsers) => {
      // ответственным может быть любой действующий сотрудник (все роли)
      const employees = allUsers.filter((u) => u.isActive);
      const me = await UsersAPI.me();
      if (!employees.find((u) => u.id === me.id)) employees.push(me);
      setUsers(employees);
    });
  }, [activePipeline]);

  // ── Polling ──────────────────────────────────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      const pid = activePipelineRef.current;
      if (pid) loadData(pid, true);
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [loadData]);

  // ── Drag & drop ──────────────────────────────────────────────
  const onDragStart = (e: React.DragEvent, deal: Deal) => {
    setDraggedDeal(deal);
    setIsDragging(true);
    e.dataTransfer.setData("dealId", String(deal.id));
  };

  const resetDrag = () => {
    setDragOverStage(null);
    setDraggedDeal(null);
    setIsDragging(false);
    setDragOverFinalZone(null);
  };

  const onDrop = async (e: React.DragEvent, stageId: number) => {
    if (!activePipeline || !draggedDeal) return;
    const dealId = Number(e.dataTransfer.getData("dealId"));
    if (isKaspiPipeline(activePipeline) && stages.find((s) => s.id === stageId)?.isFinal) {
      setKaspiIssueDeal(draggedDeal);
      resetDrag();
      return;
    }
    try {
      await DealsAPI.updateStage(dealId, { pipelineId: activePipeline, stageId });
    } catch {
      // отказ сервера (например, не хватает товара) — окно с причиной покажет api/axios.ts
    } finally {
      resetDrag();
    }
    await loadData(activePipeline, false);
  };

  const handleDropToFinal = async (type: "won" | "lost") => {
    if (!draggedDeal || !activePipeline) return;
    const dealId = draggedDeal.id;
    if (type === "won" && isKaspiPipeline(activePipeline)) {
      setKaspiIssueDeal(draggedDeal);
      resetDrag();
      return;
    }
    try {
      if (type === "won") await DealsAPI.win(dealId);
      else await DealsAPI.lose(dealId);
      setDeals((prev) => prev.filter((d) => d.id !== dealId));
    } catch {
      // сделка остаётся на доске; причину покажет окно «не хватает на складе» / сообщение сервера
    } finally {
      resetDrag();
    }
    await loadData(activePipeline, false);
  };

  // ── Filter ───────────────────────────────────────────────────
  const filteredDeals = deals.filter((d) => {
    if (!search) return true;
    const client = clients.find((c) => c.id === d.clientId);
    const name = client ? `${client.firstName ?? ""} ${client.lastName ?? ""} ${client.companyName ?? ""}`.toLowerCase() : "";
    return d.title.toLowerCase().includes(search.toLowerCase()) || name.includes(search.toLowerCase());
  });

  const fmtMoney = (n: number) => new Intl.NumberFormat("ru-RU").format(n) + " ₸";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#0a0d14" }}>
      <style>{`@keyframes slideIn { from { transform: translateX(120%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>

      {/* ── Top bar ── */}
      <div className="kanban-topbar">
        {/* Строка 1: кнопка + воронки + поиск (десктоп) + LIVE */}
        <div className="kanban-topbar-row1">
          <button onClick={() => setCreateStageId(stages[0]?.id ?? null)} className="kanban-create-btn">
            + Сделка
          </button>

          {/* Десктоп: кнопки-табы */}
          <div className="kanban-pipelines kanban-pipelines-tabs">
            {pipelines.map((p) => (
              <button key={p.id} onClick={() => setActivePipeline(p.id)} style={{
                padding: "6px 14px", borderRadius: 7, fontSize: 13, cursor: "pointer",
                border: `1px solid ${hexOk(p.color)}${activePipeline === p.id ? "" : "66"}`,
                background: activePipeline === p.id ? `${hexOk(p.color)}26` : "transparent",
                color: activePipeline === p.id ? hexOk(p.color) : "#9ca3af",
                boxShadow: activePipeline === p.id ? `0 0 0 1px ${hexOk(p.color)}55, 0 2px 10px ${hexOk(p.color)}33` : "none",
                fontWeight: activePipeline === p.id ? 600 : 400,
                whiteSpace: "nowrap", transition: "all 0.15s", flexShrink: 0,
              }}>{p.name}</button>
            ))}
          </div>
          {/* Мобильный: select */}
          <select
            className="kanban-pipelines-select"
            value={activePipeline ?? ""}
            onChange={(e) => setActivePipeline(Number(e.target.value))}
          >
            {pipelines.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <input
            type="search" placeholder="Поиск…" value={search} onChange={(e) => setSearch(e.target.value)}
            className="kanban-search-input kanban-search-desktop"
          />

          {getPermissions().canDeleteDeals && (
            <DeleteRequestsButton onChanged={() => { activePipeline && loadData(activePipeline, false); }} />
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#10b981", boxShadow: "0 0 6px #10b981" }} />
            <span style={{ fontSize: 11, color: "#4b5563", fontWeight: 600 }}>LIVE</span>
          </div>
        </div>

        {/* Строка 2: поиск только на мобильном */}
        <div className="kanban-topbar-row2">
          <input
            type="search" placeholder="Поиск сделок…" value={search} onChange={(e) => setSearch(e.target.value)}
            className="kanban-search-input"
            style={{ width: "100%", boxSizing: "border-box" }}
          />
        </div>
      </div>

      {/* ── Kanban board ── */}
      <div className="kanban-board" style={{ display: "flex", gap: 12, padding: "14px 16px", overflowX: "auto", flex: 1, alignItems: "flex-start", borderTop: `2px solid ${hexOk(pipelines.find((p) => p.id === activePipeline)?.color)}` }}>
        {stages.map((stage) => {
          const stageDeals = filteredDeals.filter((d) => d.stageId === stage.id).sort((a, b) => a.order - b.order);
          const stageTotal     = stageDeals.reduce((s, d) => s + Number(d.amount ?? 0), 0);
          const stagePrepaid   = stageDeals.reduce((s, d) => s + Number(d.prepayment ?? 0), 0);
          const stageRemaining = stageTotal - stagePrepaid;
          const isOver = dragOverStage === stage.id;
          const color = stage.color || "#6366f1";
          const isExpanded = expandedStages.has(stage.id);

          return (
            <div key={stage.id}
              onDragOver={(e) => { e.preventDefault(); setDragOverStage(stage.id); }}
              onDragLeave={() => setDragOverStage(null)}
              onDrop={(e) => onDrop(e, stage.id)}
              className="kanban-col-wrap"
              style={{
                flexShrink: 0, width: 268,
                background: isOver ? "rgba(99,102,241,0.06)" : "#0f1117",
                border: `1px solid ${isOver ? "#6366f1" : hexOk(pipelines.find((p) => p.id === activePipeline)?.color) + "88"}`,
                borderTop: `3px solid ${color}`,
                borderRadius: 12, display: "flex", flexDirection: "column", overflow: "hidden",
                transition: "border-color 0.15s, background 0.15s",
                maxHeight: "calc(100vh - 120px)",
              }}>
              {/* Column header */}
              <div
                className="kanban-col-header-wrap"
                onClick={() => isMobile() && toggleStage(stage.id)}
                style={{ padding: "10px 12px", borderBottom: isExpanded || !isMobile() ? "1px solid #1f2937" : "none", background: "#0a0d14", flexShrink: 0, cursor: "pointer" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#e5e7eb", flex: 1 }}>{stage.name}</span>
                  <span style={{ fontSize: 11, color: "#4b5563", background: "#131926", borderRadius: 10, padding: "1px 7px", fontWeight: 600 }}>
                    {stageDeals.length}
                  </span>
                  {/* Стрелка — только на мобильном */}
                  <span className="kanban-col-arrow" style={{ fontSize: 10, color: "#4b5563", transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
                  <button onClick={(e) => { e.stopPropagation(); setCreateStageId(stage.id); }} style={{
                    background: "none", border: `1px solid ${color}44`, color, borderRadius: 6,
                    fontSize: 13, cursor: "pointer", padding: "1px 8px", lineHeight: 1.6, flexShrink: 0,
                  }}>+</button>
                </div>
                <div style={{ marginTop: 5, paddingLeft: 16, display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                  {stagePrepaid > 0 ? (
                    <>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#f59e0b" }}>{fmtMoney(stageRemaining)}</span>
                      <span style={{ fontSize: 10, color: "#4b5563", textDecoration: "line-through" }}>{fmtMoney(stageTotal)}</span>
                      <span style={{ fontSize: 10, color: "#10b981" }}>аванс {fmtMoney(stagePrepaid)}</span>
                    </>
                  ) : stageTotal > 0 ? (
                    <span style={{ fontSize: 12, fontWeight: 700, color }}>{fmtMoney(stageTotal)}</span>
                  ) : null}
                </div>
              </div>

              {/* Cards — на мобильном скрыты пока не раскрыты */}
              <div className={`kanban-col-body ${isExpanded ? "kanban-col-body--open" : ""}`}
                style={{ flex: 1, overflowY: "auto", padding: "10px", display: "flex", flexDirection: "column", gap: 8 }}>
                {stageDeals.map((deal) => (
                  <div key={deal.id} draggable
                    onDragStart={(e) => onDragStart(e, deal)}
                    onDragEnd={() => { setDraggedDeal(null); setIsDragging(false); setDragOverFinalZone(null); }}
                    style={{ cursor: "grab" }}>
                    <DealCard
                      deal={deal}
                      client={clients.find((c) => c.id === deal.clientId) ?? null}
                      onEdit={() => { setEditDeal(deal); setNewDealIds((s) => { const n = new Set(s); n.delete(deal.id); return n; }); }}
                      onDelete={async (id) => {
                        const p = getPermissions();
                        if (!p.canDeleteDeals) {
                          // старший офиса: удаление только по запросу руководителю
                          if (!p.canRequestDeleteDeals) return;
                          const reason = window.prompt("Запросить удаление сделки у руководителя. Причина (необязательно):", "");
                          if (reason === null) return;
                          try {
                            await DealsAPI.requestDelete(id, reason);
                            activePipeline && loadData(activePipeline, false);
                          } catch (e: any) {
                            alert("Ошибка: " + (e?.response?.data?.message || e?.message || "неизвестная ошибка"));
                          }
                          return;
                        }
                        if (!window.confirm("Удалить сделку?")) return;
                        try {
                          await DealsAPI.delete(id);
                          activePipeline && loadData(activePipeline, false);
                        } catch (e: any) {
                          alert("Ошибка удаления: " + (e?.response?.data?.message || e?.message || "неизвестная ошибка"));
                        }
                      }}
                      isNew={newDealIds.has(deal.id)}
                    />
                  </div>
                ))}
                {stageDeals.length === 0 && (
                  <div style={{ color: "#2d3748", fontSize: 12, textAlign: "center", padding: "20px 0" }}>Нет сделок</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Final drop zones ── */}
      {isDragging && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, display: "flex", gap: 20, padding: 20, justifyContent: "center", zIndex: 500, pointerEvents: "none" }}>
          {[
            { type: "won" as const, label: "✅ Успешная сделка", color: "#10b981" },
            { type: "lost" as const, label: "❌ Проваленная сделка", color: "#ef4444" },
          ].map(({ type, label, color }) => (
            <div key={type}
              onDragOver={(e) => { e.preventDefault(); setDragOverFinalZone(type); }}
              onDragLeave={() => setDragOverFinalZone(null)}
              onDrop={() => handleDropToFinal(type)}
              style={{
                pointerEvents: "all", width: 260, padding: "16px 0",
                textAlign: "center", fontWeight: 700, fontSize: 15,
                borderRadius: 12, color, border: `2px solid ${color}`,
                background: dragOverFinalZone === type ? color + "22" : "#0f1117",
                transform: dragOverFinalZone === type ? "scale(1.05)" : "scale(1)",
                transition: "all 0.15s", cursor: "pointer",
                boxShadow: `0 4px 20px ${color}33`,
              }}>
              {label}
            </div>
          ))}
        </div>
      )}

      {/* ── Modals ── */}
      {createStageId && activePipeline && (
        <CreateDealModal
          stageId={createStageId} pipelineId={activePipeline}
          clients={clients} users={users}
          onClose={() => setCreateStageId(null)}
          onCreated={() => activePipeline && loadData(activePipeline, false)}
        />
      )}
      {kaspiIssueDeal && (
        <KaspiIssueModal
          dealId={kaspiIssueDeal.id} dealNumber={kaspiIssueDeal.number}
          onClose={() => setKaspiIssueDeal(null)}
          onDone={() => { setKaspiIssueDeal(null); activePipeline && loadData(activePipeline, false); }}
        />
      )}
      {editDeal && (
        <EditDealModal
          deal={editDeal} clients={clients} users={users}
          onClose={() => setEditDeal(null)}
          onUpdated={() => activePipeline && loadData(activePipeline, false)}
        />
      )}

      {/* ── Toasts ── */}
      <NewDealToast
        toasts={toasts} clients={clients}
        onDismiss={(id) => setToasts((prev) => prev.filter((t) => t.id !== id))}
        onOpen={(deal) => {
          setToasts((prev) => prev.filter((t) => t.deal.id !== deal.id));
          setEditDeal(deal);
          setNewDealIds((s) => { const n = new Set(s); n.delete(deal.id); return n; });
        }}
      />
    </div>
  );
}
